/**
*****************************************
*										                    *
* NAGARRO  - CSX - CONFIDENTIAL         *
*										                    *
*****************************************
*/

import { AppUtilityService } from '../../core/services/app-utility.service';
import { BehaviorSubject ,  Observable } from 'rxjs';
import { ConfigService } from '../../core/services/config.service';
import { DataStoreService } from '../../core/services/datastore.service';
import { HttpStatus } from '../../core/model/http-status-enum.model';
import { Injectable } from '@angular/core';
import { LmsLoaderService } from '../../core/services/lms-loader.service';
import { LmsLoggerService } from '../../core/services/lms-logger.service';
import { LocoDetail } from '../../core/model/loco-detail.model';
import { MessageHandlerService } from '../../core/services/message-handler.service';
import { PopupService } from '../../core/services/popup.service';
import { Router } from '@angular/router';
import { Tab } from '../../core/model/tab.model';
import { TabData } from '../../core/model/tab-data.model';
import { TrainProfileDataResponse } from '../../core/model/train-profile-data-response.model';
import { TreeNode } from 'primeng/api/treenode';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { tap } from 'rxjs/operators';

/**
 * @export
 * @class TrainProfileService
 */
@Injectable()
export class TrainProfileService {

  private RESOURCE_URL = '/trains/profile/'; // Rest Url to get Data from Database
  private configData: any; // Get config Service Data
  loadData: Observable<TrainProfileDataResponse>; // Observable used to get Data of Train Profile
  private loadDataBS: BehaviorSubject<TrainProfileDataResponse>; // BehaviorSubject used to detect any change in Page

  /**
   * Creates an instance of TrainProfileService.
   * @param {HttpClient} http
   * @param {ConfigService} configService
   * @param {DataStoreService} dataStoreService
   * @param {LmsLoggerService} lmsLoggerService
   * @param {Router} router
   * @param {LmsLoaderService} lmsLoaderService
   * @param {MessageHandlerService} messageHandlerService
   * @param {AppUtilityService} appUtilityService
   * @param {PopupService} popupService
   * @memberof TrainProfileService
   */
  constructor(private http: HttpClient,
    private configService: ConfigService,
    private dataStoreService: DataStoreService,
    private lmsLoggerService: LmsLoggerService,
    private router: Router,
    private lmsLoaderService: LmsLoaderService,
    private messageHandlerService: MessageHandlerService, private appUtilityService: AppUtilityService,
    private popupService: PopupService) {
    this.configData = this.configService.getConfigData();
    this.loadDataBS = <BehaviorSubject<TrainProfileDataResponse>>new BehaviorSubject({});
    this.loadData = this.loadDataBS.asObservable();
  }


  /**
   * @param {string} id
   * @param {Array<LocoDetail>} data
   * @returns {Observable<Response>}
   * @memberof TrainProfileService
   */
  calculateTonnageCapacity(id: string, data: Array<LocoDetail>): Observable<Response> {
    const apiUrl = this.configData.urls.API_URL;
    let headers = new HttpHeaders();
    headers = headers.append('Content-Type', 'application/json');
    const options = { headers: headers };
    this.lmsLoggerService.logInfo('TrainProfileService=>calculateTonnageCapacity service call starts');
    // return this.http.get('data/calculated-tonnage.json')
    return this.http.post<any>(`${apiUrl}${this.RESOURCE_URL}calculateTonnage/${id}`,
      JSON.stringify(data), options)
      .pipe(
        tap(() => this.lmsLoggerService.logInfo('LocoDataService=>transferLocomotive service call ends'))
      );
  }

  /**
   * This method gets data from Database through rest service
   * @param {Tab} tab
   * @returns
   * @memberOf TrainProfileService
   */
  load(tab: Tab) {
    let trainProfileData: TrainProfileDataResponse;
    let foundInDataStore = false;
    if (tab && tab.tabSearchParameter) {
      if (tab.tabSearchParameter.searchText) {
        tab.tabSearchParameter.searchText = tab.tabSearchParameter.searchText.trim();
      } else {
        this.lmsLoaderService.hideLoader();
        return;
      }
      if (tab.subTypeDetail[tab.subType.toLowerCase()].status === this.configData.tabStatus.error) {
        this.lmsLoaderService.hideLoader();
        return;
      }
      if (!this.appUtilityService.isValidProfileTrainId(tab.tabSearchParameter.searchText)) {
        this.showErrorOnInvalidProfileSearch();
      } else {

        if (tab.subTypeDetail[tab.subType.toLowerCase()].status !== this.configData.tabStatus.refresh) {
          const tabData = this.dataStoreService.getValue(tab);
          if (tabData) {
            trainProfileData = <TrainProfileDataResponse>(tabData.data[tab.subType]);
            foundInDataStore = true;
            this.lmsLoaderService.hideLoader();
            this.loadDataBS.next(trainProfileData);
          }
        } else {
          foundInDataStore = false;
        }
        if (!foundInDataStore) {
          this.dataSearch(tab);
        }
      }
    } else {
      this.lmsLoaderService.hideLoader();
      this.loadDataBS.next(null);
    }
  }

  /**
   * Show error is invalid train is searched
   * @memberof TrainProfileService
   */
  showErrorOnInvalidProfileSearch() {
    const lTrainProfileData = <TrainProfileDataResponse>({
      httpStatus: 400,
      errorMessage: this.configService.getConfigData().messages.invalidFormatTrain
    });
    this.loadDataBS.next(lTrainProfileData);
    this.lmsLoggerService.logError('TrainProfileService => load()',
      this.configService.getConfigData().messages.invalidFormatTrain);
    this.lmsLoaderService.hideLoader();
  }

  /**
   * This method actually gets data from Database
   * @param {Tab} tab
   * @memberof TrainProfileService
   */
  dataSearch(tab: Tab) {
    this.lmsLoaderService.showLoader();
    const trainId = tab.tabSearchParameter.searchText;
    this.lmsLoggerService.logInfo('TrainProfileService=>dataSearch service call starts');
    this.http.get(`${this.configService.getConfigData().urls.API_URL}${this.RESOURCE_URL}${trainId}`)
      .subscribe(data => {
        this.subscribeData(tab, data);
        this.lmsLoggerService.logInfo('TrainProfileService=>dataSearch service call ends');
      },
      error => {
        this.errorOccuredWhileGettingData(error);
      });
  }

  /**
   * This method subscribe data when we fetch data from backend
   * @param {Tab} tab
   * @param {*} data
   * @memberOf TrainProfileService
   */
  private subscribeData(tab: Tab, data: any) {
    const lTrainProfileData: TrainProfileDataResponse = this.convertListToTreeNode(data.profileData);
    const tabData = <TabData>({
      id: tab.id,
      subType: tab.subType
    });
    tabData.data = tabData.data ? tabData.data : {};
    tabData.data[tab.subType] = lTrainProfileData;
    this.lmsLoaderService.hideLoader();
    lTrainProfileData.refreshTime = data.refreshTime;
    lTrainProfileData.httpStatus = HttpStatus.OK;
    lTrainProfileData.tabId = tab.id;
    lTrainProfileData.errorMessage = this.configData.trainProfileLabel.blank;
    tab.subTypeDetail.profile.message = this.configData.trainProfileLabel.blank;
    this.dataStoreService.putValue(tab, tabData);
    if (lTrainProfileData.profileData.length > 0) {
      this.loadDataBS.next(lTrainProfileData);
    } else {
      let message;
      if (this.configData.trainProfileLabel.success === data.message) {
        /* if (tab && tab.tabSearchParameter && tab.tabSearchParameter.searchText && tab.tabSearchParameter.searchText.trim().length === 4) {
          message = this.configData.trainProfileLabel.trainProfileNotFoundFirst + tab.tabSearchParameter.searchText + this.configData.trainProfileLabel.trainProfileNotFoundSecond;
        } */
        if (tab && tab.tabSearchParameter && tab.tabSearchParameter.searchText && tab.tabSearchParameter.searchText.trim().length === 6) {
          message = this.configData.trainProfileLabel.trainInquiryFail;
        }
      } else {
        message = data.message;
      }
      const lTrainProfileDataNoData = <TrainProfileDataResponse>({
        httpStatus: 400,
        errorMessage: message
      });
      this.loadDataBS.next(lTrainProfileDataNoData);
    }
    this.lmsLoggerService.logInfo('TrainProfileService => load()',
      `Train Profile Data Fetch Successfull for ${JSON.stringify(tab)}`);
  }
  /**
   * This method tells us about the error while fetchin data
   * @private
   * @param {*} error
   * @memberOf TrainProfileService
   */
  private errorOccuredWhileGettingData(error: any) {
    this.lmsLoaderService.hideLoader();
    const lTrainProfileData = <TrainProfileDataResponse>({
      httpStatus: error.status,
      errorMessage: this.appUtilityService.getHttpErroMessage(error, false)
    });
    this.loadDataBS.next(lTrainProfileData);
    this.lmsLoggerService.logError('TrainProfileService => load()', this.appUtilityService.getHttpErroMessage(error, false));
  }

  /**
   * @memberOf TrainDataService
   */
  cleanBehaviourSubject() {
    this.loadDataBS.next(null);
  }

  /**
   * This method convert list which we are getting from backend into Tree Node
   * @param {*} dataMap
   * @returns {TrainProfileDataResponse}
   * @memberOf TrainProfileService
   */
  private convertListToTreeNode(dataMap: any): TrainProfileDataResponse {
    const lTrainProfileData: TrainProfileDataResponse = {};
    const tree: Array<TreeNode> = [];
    let i = 0;
    let j = 0;
    let historicalColorFound = false;
    if (dataMap && dataMap.LMS) {
      for (const lmsLevel of dataMap.LMS) {
        const lms: TreeNode = {};
        const childProfile: TreeNode[] = [];
        lms.children = childProfile;
        i++;
        j = 0;
        lms.data = lmsLevel;
        lms.data.level = 'LMS';
        lms.data.tonnageCapacity = this.configData.trainProfileLabel.blank;
        lms.data.legend = this.getClassforColumn(lmsLevel.mostReactiveS);
        lms.data.arriveClass = this.getClassForArrival(lmsLevel.preferredFuelLocationS);
        if (!historicalColorFound) {
          historicalColorFound = this.getMaxHistoricalColor(lmsLevel.maxHisTons80Q, lmsLevel.historicalTons80);
          lms.data.historicalColor = historicalColorFound ? this.configData.trainProfileLabel.Y : this.configData.trainProfileLabel.N;
        } else {
          lms.data.historicalColor = this.configData.trainProfileLabel.N;
        }
        lms.expanded = false;
        if (dataMap[this.configData.trainProfileLabel.profile.concat(i.toString())]) {
          for (const profileLevel of dataMap[this.configData.trainProfileLabel.profile.concat(i.toString())]) {
            const profile: TreeNode = {};
            const childGrade: TreeNode[] = [];
            profile.children = childGrade;
            j++;
            profile.data = profileLevel;
            profile.data.level = this.configData.trainProfileLabel.profile;
            profile.data.tonnageCapacity = this.configData.trainProfileLabel.blank;
            profile.data.legend = this.getClassforColumn(profileLevel.mostReactiveS);
            profile.data.arriveClass = this.getClassForArrival(profileLevel.preferredFuelLocationS);
            profile.data.historicalColor = this.configData.trainProfileLabel.N;
            profile.expanded = false;
            if (dataMap[this.configData.trainProfileLabel.grade.concat(i.toString()).concat(j.toString())]) {
              for (const gradeLevel of dataMap[this.configData.trainProfileLabel.grade.concat(i.toString()).concat(j.toString())]) {
                const grade: TreeNode = {};
                grade.data = gradeLevel;
                grade.data.level = this.configData.trainProfileLabel.grade;
                grade.data.tonnageCapacity = this.configData.trainProfileLabel.blank;
                grade.data.legend = this.getClassforColumn(gradeLevel.mostReactiveS);
                grade.data.arriveClass = this.getClassForArrival(gradeLevel.preferredFuelLocationS);
                grade.data.historicalColor = this.configData.trainProfileLabel.N;
                profile.children.push(grade);
              }
            }
            lms.children.push(profile);
          }
        }
        tree.push(lms);
      }
    }
    lTrainProfileData.profileData = tree;
    return lTrainProfileData;
  }

  /**
   * Apply Legend
   * @private
   * @param {string} maxHisTons80Q
   * @param {string} helpers
   * @returns {string}
   * @memberOf TrainProfileService
   */
  private getClassforColumn(mostReactiveS: string): string {
    if (this.configData.trainProfileLabel.Y === mostReactiveS) {
      return this.configData.trainProfileLabel.bgGreen;
    } else if (this.configData.trainProfileLabel.H === mostReactiveS) {
      return this.configData.trainProfileLabel.bgYellow;
    } else if (this.configData.trainProfileLabel.B === mostReactiveS) {
      return this.configData.trainProfileLabel.greenYellow;
    }
  }

  /**
   * Check arrival field for Aqua color
   * @private
   * @param {string} value
   * @returns
   * @memberOf TrainProfileService
   */
  private getClassForArrival(value: string) {
    if (this.configData.trainProfileLabel.Y === value) {
      return this.configData.trainProfileLabel.bgAqua;
    }
  }

  /**
   * @private
   * @param {string} maxHisTons80Q
   * @param {string} historicalTons80
   * @returns
   * @memberOf TrainProfileService
   */
  private getMaxHistoricalColor(maxHisTons80Q: string, historicalTons80: string) {
    if (maxHisTons80Q && historicalTons80 && Number(maxHisTons80Q) > 0 && (maxHisTons80Q === historicalTons80)) {
      return true;
    } else {
      return false;
    }
  }
}
